import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Trash2 } from 'lucide-react'

export default function DeleteProfile() {
  const navigate = useNavigate()
  const [selectedReason, setSelectedReason] = useState('')
  const [otherReason, setOtherReason] = useState('')
  const [confirmText, setConfirmText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const deleteReasons = [
    'I no longer need this account',
    'I have privacy concerns',
    'I found a better alternative',
    'The platform is not meeting my expectations',
    'I have too many accounts',
    'Technical issues or bugs',
    'Poor customer support',
    'Other'
  ]

  const handleDeleteProfile = async () => {
    if (confirmText !== 'DELETE') {
      alert('Please type DELETE to confirm')
      return
    }

    if (!selectedReason) {
      alert('Please select a reason for deletion')
      return
    }

    if (selectedReason === 'Other' && !otherReason.trim()) {
      alert('Please provide a reason for deletion')
      return
    }

    setIsDeleting(true)

    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/user/delete-profile', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: selectedReason === 'Other' ? otherReason : selectedReason
        })
      })

      if (response.ok) {
        // Clear localStorage
        localStorage.removeItem('token')
        localStorage.removeItem('user')

        alert('Your profile has been deleted successfully.')
        navigate('/user/login')
      } else {
        const data = await response.json()
        alert(data.error || 'Failed to delete profile')
      }
    } catch (error) {
      console.error('Error deleting profile:', error)
      alert('An error occurred while deleting your profile')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-red-100 dark:bg-red-900/50 rounded-lg">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-red-900 dark:text-red-100">
              Delete Profile
            </h1>
            <p className="text-red-700 dark:text-red-200 mt-2">
              This action is permanent and cannot be undone. All your data will be deleted including:
            </p>
            <ul className="list-disc list-inside text-red-700 dark:text-red-200 mt-2 space-y-1">
              <li>Your profile information</li>
              <li>Course enrollments and progress</li>
              <li>Certificates</li>
              <li>Payment history</li>
              <li>All personal data</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Reason Selection */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Why are you deleting your account?
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
          Your feedback helps us improve our platform
        </p>

        <div className="space-y-3">
          {deleteReasons.map((reason) => (
            <label
              key={reason}
              className="flex items-center p-4 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <input
                type="radio"
                name="reason"
                value={reason}
                checked={selectedReason === reason}
                onChange={(e) => setSelectedReason(e.target.value)}
                className="w-4 h-4 text-purple-600 focus:ring-purple-500"
              />
              <span className="ml-3 text-slate-900 dark:text-white">{reason}</span>
            </label>
          ))}
        </div>

        {/* Other Reason Text Area */}
        {selectedReason === 'Other' && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Please tell us more
            </label>
            <textarea
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              rows={4}
              placeholder="Tell us why you're leaving..."
              className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        )}
      </div>

      {/* Confirmation */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Confirm Deletion
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
          To confirm, please type <span className="font-bold text-red-600 dark:text-red-400">DELETE</span> in the box below
        </p>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="Type DELETE to confirm"
          className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={() => navigate('/user/dashboard/profile')}
          className="flex-1 px-6 py-3 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleDeleteProfile}
          disabled={isDeleting || !selectedReason || confirmText !== 'DELETE'}
          className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isDeleting ? (
            'Deleting...'
          ) : (
            <>
              <Trash2 className="h-5 w-5" />
              Delete Profile Permanently
            </>
          )}
        </button>
      </div>
    </div>
  )
}
